import { useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
  AppBar
} from '@mui/material'
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Logout as LogoutIcon
} from '@mui/icons-material'

import { authActions } from '../../redux'
import { SocrNavDrawer } from '../SocrNavDrawer'
import { SocrBoardStatusBar } from '../SocrBoardStatusBar'
import { SeverityChip } from './dtSummaryStrip'
import { formatRelativeTime } from './pageNavSwitcher'

const Stat = ({ label, value, muted = false }) => (
  <Box sx={{ minWidth: 52 }}>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: '0.58rem',
        letterSpacing: 0.45,
        textTransform: 'uppercase',
        color: alpha('#fff', 0.48),
        lineHeight: 1.1,
        mb: 0.2
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: '1.05rem',
        color: muted ? alpha('#fff', 0.4) : alpha('#fff', 0.95),
        lineHeight: 1
      }}
    >
      {value}
    </Typography>
  </Box>
)

const UntrackedStat = ({
  count,
  clients = [],
  muted = false,
  tooltipTitle = 'Not tracking'
}) => {
  const theme = useTheme()
  const body = <Stat label='Untracked' value={count} muted={muted} />
  if (!clients.length || muted) return body

  return (
    <Tooltip
      arrow
      placement='bottom-start'
      title={(
        <Box sx={{ py: 0.5, maxWidth: 280 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', mb: 0.75 }}>
            {tooltipTitle}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {clients.map((c) => (
              <Chip
                key={c.id || c.label}
                size='small'
                label={c.label}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  backgroundColor: alpha(c.color || theme.custom.keysys.blue, 0.2),
                  border: `1px solid ${alpha(c.color || theme.custom.keysys.blue, 0.4)}`,
                  color: '#fff'
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    >
      <Box sx={{ cursor: 'default' }}>{body}</Box>
    </Tooltip>
  )
}

const DependencyTrackAppBar = ({ props: viewProps = {}, controls = {} }) => {
  const {
    authUser,
    mappingsFetchedAt,
    mode = 'vulnerabilities',
    trackedCount = 0,
    untrackedCount = 0,
    untracked = [],
    boardSeverity = {},
    needsUpgradeCount = 0,
    summaryReady = false
  } = viewProps
  const { dispatch } = controls
  const theme = useTheme()
  const isCompactBar = useMediaQuery(theme.breakpoints.down('sm'))
  const isNarrowSummary = useMediaQuery(theme.breakpoints.down('md'))
  const isUpgrades = mode === 'upgrades'
  const barAccent = isUpgrades
    ? (theme.custom.colors.supportTypes.upgrade || '#ffd54f')
    : theme.custom.colors.supportTypes.vulnerability
  const hairline = alpha('#fff', 0.12)
  const [userMenuAnchor, setUserMenuAnchor] = useState(null)
  const userMenuOpen = Boolean(userMenuAnchor)

  const email = authUser?.email || ''
  const rawName = authUser?.displayName?.trim()
  const displayName = rawName || email || 'Signed in'
  const showEmailSubline = Boolean(email && rawName)
  const initial = String(displayName).slice(0, 1).toUpperCase()
  const mappingsAge = formatRelativeTime(mappingsFetchedAt)
  const muted = !summaryReady

  const boardSummary = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: { xs: 1, sm: 1.5, md: 2 },
        minWidth: 0,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1.75 }, flexShrink: 0 }}>
        <Stat label='Tracked' value={muted ? '—' : trackedCount} muted={muted} />
        <UntrackedStat
          count={muted ? '—' : untrackedCount}
          clients={untracked}
          muted={muted}
          tooltipTitle={isUpgrades ? 'Not tracking upgrades' : 'Not tracking vulnerabilities'}
        />
        {isUpgrades && !muted
          ? <Stat label='Needs upgrade' value={needsUpgradeCount} muted={muted} />
          : null}
      </Box>
      {!muted && !isUpgrades
        ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              alignItems: 'center',
              minWidth: 0
            }}
          >
            <SeverityChip
              label={isNarrowSummary ? 'C' : 'Critical'}
              value={boardSeverity.critical}
              color='#ff1744'
            />
            <SeverityChip
              label={isNarrowSummary ? 'H' : 'High'}
              value={boardSeverity.high}
              color='#f33b59'
            />
            <SeverityChip
              label={isNarrowSummary ? 'M' : 'Medium'}
              value={boardSeverity.medium}
              color='#ff9800'
            />
          </Box>
          )
        : null}
    </Box>
  )

  return (
    <AppBar
      position='sticky'
      elevation={0}
      sx={{
        borderRadius: 0,
        px: { xs: 1, sm: 1.5, md: 2 },
        border: 'none',
        borderTop: 'none',
        borderBottom: 'none',
        boxShadow: 'none'
      }}
    >
      <Container disableGutters sx={{ mx: 0, width: '100%', maxWidth: '100% !important' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.25, md: 1.75 },
            py: 0.65,
            minHeight: 48
          }}
        >
          <SocrNavDrawer />

          <Box
            sx={{
              width: '1px',
              alignSelf: 'stretch',
              my: 0.5,
              bgcolor: hairline,
              display: { xs: 'none', sm: 'block' },
              flexShrink: 0
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
            {boardSummary}
          </Box>

          {mappingsAge
            ? (
              <Typography
                variant='caption'
                sx={{
                  color: alpha('#fff', 0.45),
                  display: { xs: 'none', lg: 'block' },
                  whiteSpace: 'nowrap'
                }}
                title='Loaded with dashboard bootstrap or last Refresh from DT'
              >
                Code tree {mappingsAge}
              </Typography>
              )
            : null}

          <Button
            id='dt-user-menu-button'
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            color='inherit'
            aria-haspopup='true'
            aria-expanded={userMenuOpen ? 'true' : 'false'}
            aria-controls={userMenuOpen ? 'dt-user-menu' : undefined}
            aria-label={isCompactBar ? `Account menu for ${displayName}` : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.15, sm: 1 },
              minWidth: 0,
              maxWidth: { xs: 'none', sm: 260 },
              flexShrink: 0,
              py: 0.45,
              px: { xs: 0.35, sm: 0.85 },
              borderRadius: 1.5,
              border: `1px solid ${alpha('#fff', 0.14)}`,
              bgcolor: alpha('#000', 0.25),
              textTransform: 'none',
              '&:hover': {
                borderColor: alpha('#fff', 0.22),
                bgcolor: alpha('#000', 0.35)
              }
            }}
          >
            <Avatar
              src={isCompactBar ? undefined : (authUser?.photoURL || '')}
              sx={{
                width: { xs: 30, sm: 32 },
                height: { xs: 30, sm: 32 },
                borderRadius: 1,
                bgcolor: alpha(barAccent, 0.3),
                flexShrink: 0,
                fontSize: '0.85rem',
                fontWeight: 800
              }}
            >
              {initial}
            </Avatar>
            {!isCompactBar
              ? (
                <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                  <Typography variant='body2' sx={{ fontWeight: 700, lineHeight: 1.2, display: 'block' }} noWrap>
                    {displayName}
                  </Typography>
                  {showEmailSubline
                    ? (
                      <Typography
                        variant='caption'
                        sx={{ color: theme.palette.text.secondary, lineHeight: 1.15, display: 'block' }}
                        noWrap
                      >
                        {email}
                      </Typography>
                      )
                    : null}
                </Box>
                )
              : null}
            <KeyboardArrowDownIcon
              sx={{
                fontSize: { xs: 18, sm: 20 },
                opacity: 0.85,
                flexShrink: 0,
                transform: userMenuOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s ease'
              }}
            />
          </Button>

          <Menu
            id='dt-user-menu'
            anchorEl={userMenuAnchor}
            open={userMenuOpen}
            onClose={() => setUserMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                elevation: 8,
                sx: {
                  mt: 1,
                  minWidth: 200,
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper
                }
              }
            }}
          >
            <MenuItem
              onClick={() => {
                setUserMenuAnchor(null)
                dispatch(authActions.signOutUser())
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize='small' />
              </ListItemIcon>
              Log out
            </MenuItem>
          </Menu>
        </Box>

        {/* Mobile: summary where dashboard puts chart tabs on a second row */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            gap: 1,
            pb: 0.85,
            minWidth: 0
          }}
        >
          {boardSummary}
        </Box>

        <Box sx={{ mx: { xs: -1, sm: -1.5, md: -2 } }}>
          <SocrBoardStatusBar
            trailing={mappingsAge
              ? (
                <Typography
                  component='span'
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    letterSpacing: 0.3,
                    color: alpha('#fff', 0.55),
                    display: { xs: 'none', sm: 'inline' }
                  }}
                >
                  Code tree {mappingsAge}
                </Typography>
                )
              : null}
          />
        </Box>
      </Container>
    </AppBar>
  )
}

export { DependencyTrackAppBar }
